import { useState, useEffect, useCallback, useRef } from 'react';

export interface SequencerStep {
  id: string;
  duration: number; // ms
}

interface UseStepSequencerOptions {
  steps: SequencerStep[];
  enabled?: boolean;
  loop?: boolean;
}

interface UseStepSequencerResult {
  currentStepId: string;
  stepIndex: number;
  reset: () => void;
}

export function useStepSequencer({
  steps,
  enabled = true,
  loop = true,
}: UseStepSequencerOptions): UseStepSequencerResult {
  const [stepIndex, setStepIndex] = useState(0);
  const prevEnabledRef = useRef(enabled);

  const reset = useCallback(() => {
    setStepIndex(0);
  }, []);

  // Handle enabled transitions and step advancement in a single effect
  useEffect(() => {
    // Reset on enabled transition from false -> true
    if (enabled && !prevEnabledRef.current) {
      setStepIndex(0);
    }
    prevEnabledRef.current = enabled;

    if (!enabled || steps.length === 0) return;

    const step = steps[stepIndex];
    if (!step) return;

    const timer = setTimeout(() => {
      setStepIndex((prev) => {
        const next = prev + 1;
        if (next >= steps.length) {
          return loop ? 0 : prev;
        }
        return next;
      });
    }, step.duration);

    return () => clearTimeout(timer);
  }, [enabled, stepIndex, steps, loop]);

  return {
    currentStepId: steps[stepIndex]?.id ?? '',
    stepIndex,
    reset,
  };
}
